<template>
  <div id="EmergencyShipToOverride">
    <div class="alert alert-info">
      <p class="emergencyOverrideMessage">
        If you want an address other than the address we have on file for your
        branch to be your default ship-to for emergency orders, you can choose that here. This may be helpful
        if the address on file is a freight forwarder for stock orders, but you want emergency orders to default
        directly to your branch.
      </p>
      <label class="emergencyOverrideCheckBox">
        <input
          id="emergencyShipToToggle"
          v-model="getIsEmergencyAddress"
          type="checkbox"
          class="emergencyOverrideCheckBox"
          @click="toggleEmergencyShipToAddressDisplay"
        >
        <b-popover
          :show.sync="showPopover"
          :disabled.sync="disablePopover"
          target="emergencyShipToToggle"
          title="Delete Override Address"
        >
          Are you sure you want to delete this Emergency Ship-to override address?
          <b-button @click="deleteEmergencyShipTo">Yes</b-button>
          <b-btn @click="showPopover = !showPopover">No</b-btn>
        </b-popover>
        Override Default Emergency Ship-to
      </label>
    </div>
    <div id="showHideOverride" class="emergencyFieldsHidden">
      <transition name="slide">
        <div v-if="isEmergencyAddress" class="form-group">
          <b-row>
            <label class="control-label col-sm-3 col-xs-12">Address Line 1:</label>
            <div class="form-control-static col-sm-9 col-xs-12">
              <span id="AddressLine1">{{ EmergencyShipToOverrideAddress.AddressLine1 }}</span>
            </div>
          </b-row>
          <b-row>
            <label class="control-label col-sm-3 col-xs-12">Address Line 2:</label>
            <div class="form-control-static col-sm-9 col-xs-12">
              <span id="AddressLine2">{{ EmergencyShipToOverrideAddress.AddressLine2 }}</span>
            </div>
          </b-row>
          <b-row>
            <label class="control-label col-sm-3 col-xs-12">City:</label>
            <div class="form-control-static col-sm-9 col-xs-12">
              <span id="City">{{ EmergencyShipToOverrideAddress.City }}</span>
            </div>
          </b-row>
          <b-row>
            <label class="control-label col-sm-3 col-xs-12">Country:</label>
            <div class="form-control-static col-sm-9 col-xs-12">
              <span id="Country">{{ EmergencyShipToOverrideAddress.Country }}</span>
            </div>
          </b-row>
          <b-row>
            <label class="control-label col-sm-3 col-xs-12">State/Province:</label>
            <div class="form-control-static col-sm-9 col-xs-12">
              <span id="State">{{ EmergencyShipToOverrideAddress.State }}</span>
            </div>
          </b-row>
          <b-row>
            <label class="control-label col-sm-3 col-xs-12">Postal Code:</label>
            <div class="form-control-static col-sm-9 col-xs-12">
              <span id="PostalCode">{{ EmergencyShipToOverrideAddress.PostalCode }}</span>
            </div>
          </b-row>
          <DnFunctionButton
            button-id="saveEmergencyButton"
            :callback-fn="toggleModal"
            text="Edit"
            :size="smallSize"
          />
        </div>
      </transition>
    </div>
    <CustomerAddressModal
      v-if="showModal"
      :customer-address-dto-prop="EmergencyShipToOverrideAddress"
      :selected-country-prop="getCountryLabel(EmergencyShipToOverrideAddress.Country)"
      :title="customerAddressModalTitle"
      :show-name="hideCustName"
      @close="showModal = false"
      @save="saveEmergencyAddress"
    />
  </div>
</template>

<script>
  import { mapGetters } from "vuex"
  import { AddressBookService } from "@/services/AddressBookService"
  import CustomerAddressModal from "@/components/Modals/CustomerAddressModal"
  import DnFunctionButton from "@/components/Elements/DnFunctionButton"
  import { countryHelper } from "@/components/mixins/countryHelper"
  import { sizeHelper } from "@/components/mixins/sizeHelper"
  import { ErrorLogService } from "@/services/ErrorLogService"

  export default {
    components: {
      CustomerAddressModal,
      DnFunctionButton
    },
    mixins: [countryHelper, sizeHelper],
    data: () => ({
      EmergencyShipToOverrideAddress: {
        Id: "",
        AddressLine1: "",
        AddressLine2: "",
        City: "",
        Country: "",
        State: "",
        PostalCode: ""
      },
      showModal: false,
      isEmergencyAddress: false,
      showPopover: false,
      disablePopover: true,
      hideCustName: false,
      customerAddressModalTitle: "Edit Emergency Order Address"
    }),
    computed: {
      ...mapGetters(["user", "activeAccountId"]),
      getIsEmergencyAddress: {
        get: function () {
          return this.isEmergencyAddress
        },
        set: function (newValue) {
          this.isEmergencyAddresss = newValue
        }
      },
    },
    mounted () {
      this.getEmergencyShipTo()
    },
    methods: {
      getEmergencyShipTo () {
        return AddressBookService.GetEmergencyShipToAddress(this.activeAccountId, this.user.UserId)
          .then(addressResponseDto => {
            if (addressResponseDto.AddressLine1) {
              this.EmergencyShipToOverrideAddress = addressResponseDto
              this.isEmergencyAddress = true
            }
          })
          .catch(err => {
            ErrorLogService.LogException(err)
            return []
          })
      },
      saveEmergencyAddress (addressDto) {
        // Here we set this address to the one we get back from the modal since the dto is formatted this way
        this.EmergencyShipToOverrideAddress = addressDto
        this.EmergencyShipToOverrideAddress.State = addressDto.StateProvince
        AddressBookService.SaveEmergencyShipToAddress(this.activeAccountId, this.user.UserId, addressDto).then(savedResult => {
          this.showModal = false
          this.getEmergencyShipTo()
          return savedResult
        })
          .catch(err => {
            ErrorLogService.LogException(err)
          })
      },
      deleteEmergencyShipTo () {
        if (this.isEmergencyAddress === true) {
          AddressBookService.DeleteEmergencyShipToAddress(this.activeAccountId, this.user.UserId, this.EmergencyShipToOverrideAddress.Id).then(savedResult => {
            this.isEmergencyAddress = false
            this.showPopover = false
            this.disablePopover = true
            this.EmergencyShipToOverrideAddress = {}
            return savedResult
          })
            .catch(err => {
              ErrorLogService.LogException(err)
              return []
            })
        }
      },
      toggleModal () {
        this.showModal = !this.showModal
      },
      toggleEmergencyShipToAddressDisplay () {
        // here we need to only display the pop over asking about deleting the address when data is there
        // the first if check is if they checked the box and the changed their mind
        // second one actually shows the pop over
        // third one disabales it when adding a address for the first time
        if (this.isEmergencyAddress && this.EmergencyShipToOverrideAddress.AddressLine1 == "") {
          this.disablePopover = true
          this.showPopover = false
          this.isEmergencyAddress = false
        } else if (this.isEmergencyAddress) {
          this.disablePopover = false
          this.showPopover = true
        } else {
          this.disablePopover = true
          this.showPopover = false
          this.isEmergencyAddress = true
        }
      }
    },
  }
</script>
